import _ from 'lodash';
import angular from 'angular';
import template from './template.html';
import './style.scss';
import Logger from '../../../shared/logger';

export class CandidatesCsvExport {
    csvData = {
        fileName: '',
        headers: [],
        columns: [],
    };
    publicEmailDomains = [];

    /* @ngInject */
    constructor($filter, $scope, CandidatesListService, RouteService, NotificationService) {
        this.$filter = $filter;
        this.candidatesListService = CandidatesListService;
        this.routeService = RouteService;
        this.notificationService = NotificationService;
    }

    async $onInit() {
        const { result: publicEmailDomains } = await this.routeService.getPublicEmailDomains();
        this.publicEmailDomains = publicEmailDomains;
        this.setupCsv();
    }

    $onChanges() {
        this.setupCsv();
    }

    setupCsv() {
        this.csvFileName = this.candidatesListService.getCsvFileName({
            workflowName: this.workflowName,
            jobTitle: this.jobTitle,
            employerName: this.employerName,
        });
        this.csvData = this.candidatesListService.getCvsMetadata(this.isSearchPage);
    }

    exportCandidates() {
        this.isLoading = true;

        const promiseMethod = this.isSearchPage
            ? this.getSearchCandidatesDataToExport
            : this.getDataToExport;

        return promiseMethod.bind(this)(this.allCandidatesCheckboxIsChecked)
            .then(data => Promise.resolve(data))
            .catch((error) => {
                Logger.error(error);
                this.notificationService.error('CSV_EXPORT_ERROR');
                return Promise.reject();
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    async getSearchCandidatesDataToExport() {
        const {
            workflowName,
            positionData,
            isEmployerMode,
            recruiterId,
        } = this;

        const jobTitle = _.get(positionData, 'jobTitle');
        const employerName = _.get(positionData, 'employerData.name');
        const { _id: positionId } = positionData;

        this.csvFileName = this.candidatesListService.getCsvFileName({ workflowName, jobTitle, employerName });

        const talentIds = this.talentIds || [];
        const talentsIdsToFetch = this.talentsIdsPromise ? await this.talentsIdsPromise : talentIds;

        try {
            const searchCandidates = await this.candidatesListService.getSearchCandidates({
                positionId,
                talentIds: talentsIdsToFetch,
                isEmployerMode,
                recruiterId,
                maxItems: this.maxItems,
                filters: this.additionalFilters,
            });

            return this.candidatesListService.getCandidatesCsvData({
                candidates: searchCandidates,
                columns: this.csvData.columns,
                publicEmailDomains: this.publicEmailDomains,
                positionId,
                isEmployerMode: this.isEmployerMode,
                alphabeticalOrderByName: false,
            });
        } catch (err) {
            throw new Error('[candidatesCsvExport.getSearchCandidatesDataToExport] -- Can\'t fetch candidates');
        }
    }

    async getDataToExport() {
        try {
            const {
                workflowName,
                positionData,
                isEmployerMode,
                recruiterId,
                alphabeticalOrderByName,
            } = this;

            const isFullListExport = !workflowName;
            const jobTitle = _.get(positionData, 'jobTitle');
            const employerName = _.get(positionData, 'employerData.name');
            const { _id: positionId } = positionData;

            this.csvFileName = this.candidatesListService.getCsvFileName({ workflowName, jobTitle, employerName });

            if (isFullListExport) {
                this.candidates = await this.candidatesListService.getCandidates({
                    positionId,
                    isEmployerMode,
                    recruiterId,
                    isExportAll: true,
                });
            } else {
                const noDataCandidateTalentIds = this.candidates
                    .filter(candidate => !candidate.hasOwnProperty('candidateData') && candidate.check)
                    .map(candidate => (candidate.talentId || candidate.talentID));

                if (!_.isEmpty(noDataCandidateTalentIds)) {
                    const fetchedCandidates = await this.candidatesListService.getCandidates({
                        positionId,
                        talentIds: noDataCandidateTalentIds,
                        isEmployerMode,
                        recruiterId,
                        maxItems: this.maxItems,
                    });
                    const fetchedCandidatesMap = _.keyBy(fetchedCandidates, 'talentId');
                    this.candidates = _.map(this.candidates, (candidate) => {
                        const talentId = candidate.talentId || candidate.talentID;
                        const fetchedCandidate = fetchedCandidatesMap[talentId];
                        if (fetchedCandidate) {
                            return {
                                ...candidate,
                                ...fetchedCandidate,
                            };
                        }
                        return candidate;
                    });
                }
            }

            const checkedCandidates = (this.candidates || []).reduce((candidates, candidate) => {
                if (isFullListExport || candidate.check) {
                    return [
                        ...candidates,
                        candidate,
                    ];
                }

                return candidates;
            }, []);

            const workflows = (checkedCandidates || []).map(({ workflow }) => workflow);
            const candidatesToExport = await this.candidatesListService.addPublicApprovalLinksToCandidates({
                positionId,
                candidates: checkedCandidates,
                workflows,
                publicEmailDomains: this.publicEmailDomains,
            });

            return this.candidatesListService.getCandidatesCsvData({
                candidates: candidatesToExport,
                columns: this.csvData.columns,
                publicEmailDomains: this.publicEmailDomains,
                positionId,
                isEmployerMode: this.isEmployerMode,
                alphabeticalOrderByName,
            });
        } catch (err) {
            throw new Error(err);
        }
    }
}

export const CandidatesCsvExportConfiguration = {
    template,
    bindings: {
        allCandidatesCheckboxIsChecked: '<',
        talentsIdsPromise: '<',
        additionalFilters: '<',
        talentIds: '<',
        candidates: '<',
        workflowName: '<',
        positionData: '<',
        isEmployerMode: '<',
        recruiterId: '<',
        displayMode: '@',
        linkTitle: '@',
        linkText: '@',
        alphabeticalOrderByName: '<',
        maxItems: '<',
        isSearchPage: '<',
    },
    controller: CandidatesCsvExport,
    controllerAs: 'candidatesCsvExportCtrl',
};

angular
    .module('TALENYA-FE')
    .component('candidatesCsvExport', CandidatesCsvExportConfiguration);
